import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const CurrencyContext = createContext(null);
const LOCALE = { USD: "en-US", EUR: "de-DE", GBP: "en-GB", CHF: "de-CH" };

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("nt-currency") || "USD");
  const [rates, setRates] = useState({ USD: 1 });
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    api.get("/fx/rates?base=USD").then((r) => { setRates(r.data.rates); setUpdatedAt(r.data.updated_at); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.currency && user.currency !== currency) {
      setCurrencyState(user.currency);
      localStorage.setItem("nt-currency", user.currency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.currency]);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    localStorage.setItem("nt-currency", c);
    if (user) api.put("/settings/preferences", { currency: c }).catch(() => {});
  }, [user]);

  const convert = useCallback((usd) => (usd == null ? usd : usd * (rates[currency] || 1)), [rates, currency]);

  const money = useCallback((usd, dp = 2) => {
    if (usd == null || isNaN(usd)) return "—";
    const val = usd * (rates[currency] || 1);
    try {
      return new Intl.NumberFormat(LOCALE[currency] || "en-US", { style: "currency", currency, minimumFractionDigits: dp, maximumFractionDigits: dp }).format(val);
    } catch { return `${currency} ${val.toFixed(dp)}`; }
  }, [rates, currency, lang]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, convert, money, updatedAt, supported: ["USD", "EUR", "GBP", "CHF"] }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
